import {
  computePipeSectionMAWP,
  computePipeSectionThicknessFromMAWP,
} from 'features/indicationTriage/cards/ffsCalc/calculations/mawp/calculatePipingMawp'
import {
  finiteOrNull,
  measToNumber,
  toMeas,
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValue } from 'features/libra/ontology/ontologyFieldTypes'
import { StepValidationResult } from 'features/libra/workflow/workflowStepsApi'
import {
  GeckoComponentModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

const WORKING_UNIT_RATIO = GeckoMeasurementUnit.Ratio

const PRESSURE_FIELD = 'designPressure'
const YIELD_FIELD = 'yieldStrength'
const QUALITY_FACTOR_FIELD = 'qualityFactor'
const PRESSURE_COEFFICIENT_FIELD = 'pressureCoefficient'
const TSL_FIELD = 'tsl'

interface ComputeMinThicknessValidationArgs {
  /**
   * Typed asset-model component (e.g. `GeckoPipeSection`,
   * `GeckoPressureVesselShell`). Authoritative source for every intrinsic
   * input: geometry (`radius`, `wallNominalThickness`) plus the operating
   * params `designPressure` (P), `yieldStrength` (S_y), `jointEfficiency` (E)
   * and `pressureCoefficient` (Y). `tsl` is also read here (or from legacy
   * `fieldValues`) if present, but defaults to 0 -- see `tsl_mm` below. Comes
   * from `useFfsTicketContext().componentModel` (the ticket's asset-model
   * snapshot).
   */
  componentModel: GeckoComponentModel | undefined
  /**
   * Legacy fallback only. Tickets created before the Data Gathering step was
   * retired stored the operating params here (`designPressure`,
   * `yieldStrength`, `qualityFactor`, `pressureCoefficient`, `tsl`); we read
   * them iff the asset-model snapshot doesn't carry the value. New tickets get
   * everything from `componentModel`.
   */
  fieldValues: Record<string, unknown> | undefined
  /**
   * Canonical-mm minimum measured remaining thickness, from the thickness
   * CDF (`useObservedMinThickness`). Null/undefined when not yet ready --
   * the re-rated MAWP_r results are then simply omitted (no error).
   */
  observedMinThicknessMm?: number | null
}

export interface MinimumThicknessInputs {
  outerDiameter: GeckoMeasurement | null
  nominalThickness: GeckoMeasurement | null
  designPressure: GeckoMeasurement | null
  yieldStrength: GeckoMeasurement | null
  qualityFactor: GeckoMeasurement | null
  pressureCoefficient: GeckoMeasurement | null
  weldStrengthReduction: GeckoMeasurement | null
  tsl: GeckoMeasurement | null
  /** Raw numbers for substituted-formula display, in working units. */
  raw: {
    D_mm: number | null
    tNom_mm: number | null
    P_MPa: number | null
    Sy_MPa: number | null
    E: number | null
    Y: number | null
    W_ratio: number | null
    tMeas_mm: number | null
  }
}

export interface MinimumThicknessResult {
  tMinC: GeckoMeasurement | null
  tMinL: GeckoMeasurement | null
  tMin: GeckoMeasurement | null
  mawpRC: GeckoMeasurement | null
  mawpRL: GeckoMeasurement | null
  mawpR: GeckoMeasurement | null
}

interface ComputeMinThicknessValidationResult {
  validation: StepValidationResult
  inputs: MinimumThicknessInputs
  results: MinimumThicknessResult
  missingInputs: ReadonlyArray<string>
}

const readNumber = (
  fieldValues: Record<string, unknown>,
  key: string
): number | null => {
  const raw = unwrapFieldValue(fieldValues[key] as FieldValue | undefined)
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

/**
 * Validates and computes the Minimum Thickness Analysis step:
 *
 *   t_min^C = (P * D) / (2 * S_y * E * W + 2 * P * Y)
 *   t_min^L = (P * D) / (4 * S_y * E * W + 4 * P * Y) + t_sl
 *   MAWP_r^C = (2 * S_y * E * W * t_mm) / (D - 2 * Y * t_mm)
 *   MAWP_r^L = (4 * S_y * E * W * (t_mm - t_sl)) / (D - 4 * Y * (t_mm - t_sl))
 *
 * Reuses the pure functions from
 * `features/indicationTriage/cards/ffsCalc/calculations/mawp/calculatePipingMawp`.
 * All inputs — geometry (D, t_nom) AND the operating params (P, S_y, E, Y) —
 * come from the typed asset-model `componentModel` (the ticket's snapshot);
 * `fieldValues` is consulted only as a legacy fallback for tickets created
 * before the Data Gathering step was retired. Both are passed in by the step
 * component — this is a plain function. The caller wraps the call in `useMemo`
 * for React-side memoization.
 *
 * `t_sl` (thickness for supplemental loads) defaults to 0 and is no longer a
 * required input: per SME guidance, supplemental loads are assumed not to
 * govern, so it is not collected in the Asset Model step. A stored/legacy
 * value is still honored if present, so the longitudinal branch remains
 * non-binding but correct.
 */
export function computeMinThicknessValidation({
  componentModel,
  fieldValues,
  observedMinThicknessMm,
}: ComputeMinThicknessValidationArgs): ComputeMinThicknessValidationResult {
  const fvRecord = (fieldValues ?? {}) as Record<string, unknown>

  // All intrinsic inputs come off the typed asset-model component. `radius`,
  // `wallNominalThickness`, `designPressure`, `yieldStrength` and `tsl` are
  // `GeckoMeasurement` instances (convert to working units); `jointEfficiency`
  // (E) and `pressureCoefficient` (Y) are plain ratios.
  const cm = componentModel as
    | {
        radius?: GeckoMeasurement
        wallNominalThickness?: GeckoMeasurement
        designPressure?: GeckoMeasurement
        yieldStrength?: GeckoMeasurement
        jointEfficiency?: number
        pressureCoefficient?: number
        weldStrengthReduction?: number
        tsl?: GeckoMeasurement
      }
    | undefined
  const radiusMm = measToNumber(cm?.radius, WORKING_UNIT_THICKNESS)
  const D_mm = radiusMm !== null ? radiusMm * 2 : null
  const tNom_mm = measToNumber(cm?.wallNominalThickness, WORKING_UNIT_THICKNESS)

  // Operating params: asset-model snapshot first (the post-cutover home), then
  // legacy ticket `fieldValues` for pre-cutover tickets. `??` keeps a real 0
  // from the snapshot rather than falling through to the legacy value.
  const P_MPa =
    measToNumber(cm?.designPressure, WORKING_UNIT_PRESSURE) ??
    readNumber(fvRecord, PRESSURE_FIELD)
  const Sy_MPa =
    measToNumber(cm?.yieldStrength, WORKING_UNIT_PRESSURE) ??
    readNumber(fvRecord, YIELD_FIELD)
  const E =
    finiteOrNull(cm?.jointEfficiency) ??
    readNumber(fvRecord, QUALITY_FACTOR_FIELD)
  const Y =
    finiteOrNull(cm?.pressureCoefficient) ??
    readNumber(fvRecord, PRESSURE_COEFFICIENT_FIELD)
  // t_sl defaults to 0 when absent -- SME: supplemental loads are assumed not
  // to govern, so it is no longer collected. A stored/legacy value is still
  // honored so pre-existing tickets keep computing the same result.
  const tsl_mm =
    measToNumber(cm?.tsl, WORKING_UNIT_THICKNESS) ??
    readNumber(fvRecord, TSL_FIELD) ??
    0
  // W is non-gating: absence defaults to 1.0 (no reduction), never falls
  // through to a legacy `fieldValues` key (there isn't one).
  const W = finiteOrNull(cm?.weldStrengthReduction) ?? 1

  const inputs: MinimumThicknessInputs = {
    outerDiameter: toMeas(D_mm, WORKING_UNIT_THICKNESS),
    nominalThickness: toMeas(tNom_mm, WORKING_UNIT_THICKNESS),
    designPressure: toMeas(P_MPa, WORKING_UNIT_PRESSURE),
    yieldStrength: toMeas(Sy_MPa, WORKING_UNIT_PRESSURE),
    qualityFactor: toMeas(E, WORKING_UNIT_RATIO),
    pressureCoefficient: toMeas(Y, WORKING_UNIT_RATIO),
    weldStrengthReduction: toMeas(W, WORKING_UNIT_RATIO),
    tsl: toMeas(tsl_mm, WORKING_UNIT_THICKNESS),
    raw: {
      D_mm,
      tNom_mm,
      P_MPa,
      Sy_MPa,
      E,
      Y,
      W_ratio: W,
      tMeas_mm: observedMinThicknessMm ?? null,
    },
  }

  const missingInputs: string[] = []
  if (!componentModel)
    missingInputs.push(
      'Asset Model -- component not yet added to the asset model'
    )
  if (componentModel && D_mm === null)
    missingInputs.push('Outer diameter (Asset Model)')
  if (P_MPa === null) missingInputs.push('Design Pressure (Asset Model)')
  if (Sy_MPa === null) missingInputs.push('Allowable Stress (Asset Model)')
  if (E === null) missingInputs.push('Joint Efficiency (Asset Model)')
  if (Y === null) missingInputs.push('Pressure Coefficient (Asset Model)')

  const ready = missingInputs.length === 0

  // Compute t_min (inverse of MAWP given design pressure).
  const tMinC = ready
    ? computePipeSectionThicknessFromMAWP({
        mawp: inputs.designPressure,
        yieldStrength: inputs.yieldStrength,
        qualityFactor: inputs.qualityFactor,
        outerDiameter: inputs.outerDiameter,
        pressureCoefficient: inputs.pressureCoefficient,
        weldStrengthReduction: inputs.weldStrengthReduction,
        tsl: inputs.tsl,
        axis: 'x',
      })
    : null
  const tMinL = ready
    ? computePipeSectionThicknessFromMAWP({
        mawp: inputs.designPressure,
        yieldStrength: inputs.yieldStrength,
        qualityFactor: inputs.qualityFactor,
        outerDiameter: inputs.outerDiameter,
        pressureCoefficient: inputs.pressureCoefficient,
        weldStrengthReduction: inputs.weldStrengthReduction,
        tsl: inputs.tsl,
        axis: 'y',
      })
    : null

  const tMinCNum = tMinC?.toNumber(WORKING_UNIT_THICKNESS) ?? null
  const tMinLNum = tMinL?.toNumber(WORKING_UNIT_THICKNESS) ?? null
  const tMinNum =
    tMinCNum !== null && tMinLNum !== null ? Math.max(tMinCNum, tMinLNum) : null
  const tMin = toMeas(tMinNum, WORKING_UNIT_THICKNESS)

  // Re-rated MAWP (MAWP_r) at the minimum measured remaining thickness --
  // purely informational; omitted (null) when the observed minimum isn't
  // ready, with no effect on `missingInputs` / `invalidInputs`.
  const tMeas = toMeas(observedMinThicknessMm ?? null, WORKING_UNIT_THICKNESS)
  const mawpRC =
    ready && tMeas !== null
      ? computePipeSectionMAWP({
          t: tMeas,
          tsl: inputs.tsl,
          yieldStrength: inputs.yieldStrength,
          qualityFactor: inputs.qualityFactor,
          outerDiameter: inputs.outerDiameter,
          pressureCoefficient: inputs.pressureCoefficient,
          weldStrengthReduction: inputs.weldStrengthReduction,
          axis: 'x',
        })
      : null
  const mawpRL =
    ready && tMeas !== null
      ? computePipeSectionMAWP({
          t: tMeas,
          tsl: inputs.tsl,
          yieldStrength: inputs.yieldStrength,
          qualityFactor: inputs.qualityFactor,
          outerDiameter: inputs.outerDiameter,
          pressureCoefficient: inputs.pressureCoefficient,
          weldStrengthReduction: inputs.weldStrengthReduction,
          axis: 'y',
        })
      : null

  const mawpRCNum = mawpRC?.toNumber(WORKING_UNIT_PRESSURE) ?? null
  const mawpRLNum = mawpRL?.toNumber(WORKING_UNIT_PRESSURE) ?? null
  const mawpRNum =
    mawpRCNum !== null && mawpRLNum !== null
      ? Math.min(mawpRCNum, mawpRLNum)
      : null
  const mawpR = toMeas(mawpRNum, WORKING_UNIT_PRESSURE)

  const results: MinimumThicknessResult = {
    tMinC,
    tMinL,
    tMin,
    mawpRC,
    mawpRL,
    mawpR,
  }

  // Physical / numeric validity beyond presence -- non-positive D / S_y / E
  // or a non-finite computed t_min would produce nonsense results and
  // silently let the user advance.
  const invalidInputs: string[] = []
  if (ready) {
    if ((D_mm ?? 0) <= 0) invalidInputs.push('Outer diameter must be > 0.')
    if ((P_MPa ?? 0) <= 0) invalidInputs.push('Design pressure must be > 0.')
    if ((Sy_MPa ?? 0) <= 0) invalidInputs.push('Yield strength must be > 0.')
    if ((E ?? 0) <= 0) invalidInputs.push('Quality factor must be > 0.')
    if ((Y ?? -1) < 0) invalidInputs.push('Pressure coefficient must be >= 0.')
    if ((tsl_mm ?? -1) < 0)
      invalidInputs.push('Longitudinal stress allowance must be >= 0.')
    if (tMinNum !== null && (!Number.isFinite(tMinNum) || tMinNum <= 0))
      invalidInputs.push('Computed t_min is not a valid positive value.')
  }

  let validation: StepValidationResult = undefined
  if (missingInputs.length > 0) {
    validation = {
      status: 'Error',
      message: `Missing inputs: ${missingInputs.join(', ')}.`,
    }
  } else if (invalidInputs.length > 0) {
    validation = {
      status: 'Error',
      message: `Invalid inputs: ${invalidInputs.join(' ')}`,
    }
  }

  return { validation, inputs, results, missingInputs }
}
